import {
  CabinCategoryCfmModel,
  CruiseDetailsContainerWrapperProps,
  IGetShipsResponse,
} from '@msc-b2c/interfaces/types';
import {
  fetchContentFragmentsFromCms,
  getActiveSession,
  myAreaCabinDetails,
  RootState,
  setIsErrorModalOpen,
  setShipCF,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import CruiseDetailsContainer from './CruiseDetailsContainer';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IFetchShipsFromFragmentsURLParams } from '@msc-b2c/interfaces/interfaces';
import { skipToken } from '@reduxjs/toolkit/query';
import { getRouteToNavigate } from '../navigationmenu/utils/navigationMenuUtils';
import { useNavigate } from 'react-router-dom';
import { getCabinObjectOfTheLoggedUser } from '../utils/myAreaUtils';

export function CruiseDetailsContainerWrapper({
  children,
}: Readonly<CruiseDetailsContainerWrapperProps>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { noLinkedBookingLink, navigationItems } = useSelector(
    (state: RootState) => state.navigationMenuData
  );
  const { bookingNumber = '' } = getActiveSession();

  // GET BOOKING API CALL
  const { data: booking, isLoading } = useGetBookingQuery(
    bookingNumber !== '' ? bookingNumber : skipToken
  );

  //Receive CF Data for all ships and then dispatch the ship from the current cruise
  //TODO: Handle multiple cruises scenario
  const fetchShipDataFromFragments = useCallback(() => {
    const shipCode = booking?.cabins?.[0]?.cruise?.ship?.code.slice(0, 2) ?? '';
    if (!shipCode) return;

    fetchContentFragmentsFromCms<IGetShipsResponse, IFetchShipsFromFragmentsURLParams>('getShips', {
      shipCode,
    })
      .then((result) => {
        const shipList = result?.data?.shipCfmList?.items;
        const selectedShip = shipList?.[0];

        if (selectedShip) {
          dispatch(setShipCF(selectedShip));
        } else {
          dispatch(setIsErrorModalOpen(true));
        }

        const cabinOfTheLoggedUser = getCabinObjectOfTheLoggedUser(booking);
        const categoryCode = cabinOfTheLoggedUser?.categoryCode;
        const cabinCategoryInterior =
          result?.data?.shipCfmList?.items?.[0]?.interiorCategoryCf?.find(
            (item) => item.cabinCode === categoryCode
          );
        const cabinCategoryOceanView =
          result?.data?.shipCfmList?.items?.[0]?.oceanViewCategoryCf?.find(
            (item) => item.cabinCode === categoryCode
          );
        const cabinCategoryBalcony = result?.data?.shipCfmList?.items?.[0]?.balconyCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );
        const cabinCategorySuite = result?.data?.shipCfmList?.items?.[0]?.suiteCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );
        const cabinCategoryYachtClub =
          result?.data?.shipCfmList?.items?.[0]?.yachtClubCategoryCf?.find(
            (item) => item.cabinCode === categoryCode
          );

        const cabinCategory: CabinCategoryCfmModel | undefined =
          cabinCategoryInterior ??
          cabinCategoryOceanView ??
          cabinCategoryBalcony ??
          cabinCategorySuite ??
          cabinCategoryYachtClub ??
          undefined;

        dispatch(myAreaCabinDetails(cabinCategory));
      })
      .catch((error) => {
        console.error('Error fetching ship content fragment:', error);
        dispatch(setIsErrorModalOpen(true));
      });
  }, [booking]);

  useEffect(() => {
    if (!isLoading) {
      if (booking?.cabins?.[0]?.cruise?.id) {
        fetchShipDataFromFragments();
      } else {
        const route = getRouteToNavigate(navigationItems || [], noLinkedBookingLink?.url ?? '');
        navigate(`${route}${globalThis.location.href.includes('.html') ? '.html' : ''}`);
      }
    }
  }, [booking, fetchShipDataFromFragments]);

  return booking && <CruiseDetailsContainer>{children}</CruiseDetailsContainer>;
}

export default CruiseDetailsContainerWrapper;

CruiseDetailsContainerWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/cruisedetailscontainer';
